// GraphModal.tsx
import React, { useState, useEffect } from "react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer
} from "recharts";

interface Props {
  title: string;
  value: number;
  dataType: string;
  onClose: () => void;
}

interface DataPoint {
  date: string;
  value: number;
}

// Helper function to determine if a data type should use number format (#) or currency ($)
const shouldUseNumberFormat = (dataType: string): boolean => {
  // Ticket count metrics - should show as numbers with commas
  const numberFormatTypes = [
    "pending", "listed", "sold", "settlement", "delisted", "unsold",
    "To Be Verified", "Verified", "Listed", "Sold", "Delisted", "Unsold",
    "pending tickets", "listed tickets", "sold tickets", "settled tickets",
    "tobeverified", "verified_count", "listed_count", "sold_count",
    "pending_tickets", "listed_tickets", "sold_tickets", "total_pending"
  ];
  
  // Revenue/fee metrics - should show with $ sign
  const currencyFormatTypes = [
    "revenue", "payout", "profit", "fee", "potential", "avgprice",
    "total revenue", "user payout", "qa profit", "vl fee", "total potential"
  ];
  
  const dataTypeLower = dataType?.toLowerCase() || "";
  
  // Check if it's a number format type
  for (const type of numberFormatTypes) {
    if (dataTypeLower.includes(type.toLowerCase())) {
      return true;
    }
  }
  
  // Check if it's a currency format type
  for (const type of currencyFormatTypes) {
    if (dataTypeLower.includes(type.toLowerCase())) {
      return false;
    }
  }
  
  // Default: check for currency indicators
  if (dataTypeLower.includes("price") || dataTypeLower.includes("amount")) {
    return false; // currency
  }
  
  // Default to number format for unknown types
  return true;
};

const GraphModal: React.FC<Props> = ({ title, value, dataType, onClose }) => {
  const [range, setRange] = useState("30");
  const [chartData, setChartData] = useState<DataPoint[]>([]);
  
  const isNumberFormat = shouldUseNumberFormat(dataType);
  const isCurrency = !isNumberFormat;

  // Generate realistic historical data based on current value and data type
  const generateHistoricalData = (currentValue: number, days: number): DataPoint[] => {
    const data: DataPoint[] = [];
    const today = new Date();
    
    // Different volatility patterns based on data type
    let volatility = 0.2; // Default 20% volatility
    
    if (isCurrency) {
      volatility = 0.15; // Less volatile for revenue metrics
    } else {
      volatility = 0.25; // More volatile for ticket counts
    }
    
    for (let i = days; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      
      // Create trend patterns based on data type
      let trend = 1.0;
      if (isCurrency && (dataType === "revenue" || dataType === "profit")) {
        trend = 0.85 + (0.3 * (i / days)); // Upward trend
      } else if (!isCurrency && dataType === "pending") {
        trend = 1.1 - (0.2 * (i / days)); // Downward trend
      }
      
      const variation = 1 - volatility + (Math.random() * volatility * 2);
      const finalValue = Math.round(currentValue * trend * variation);
      
      data.push({
        date: `${date.getMonth() + 1}/${date.getDate()}`,
        value: finalValue
      });
    }
    
    return data;
  };

  useEffect(() => {
    setChartData(generateHistoricalData(value, parseInt(range)));
  }, [value, range, dataType]);

  const handleRangeChange = (newRange: string) => {
    setRange(newRange);
    setChartData(generateHistoricalData(value, parseInt(newRange)));
  };

  // Calculate statistics
  const currentValue = chartData.length > 0 ? chartData[chartData.length - 1].value : value;
  const startValue = chartData.length > 0 ? chartData[0].value : value;
  const change = startValue > 0 ? (((currentValue - startValue) / startValue) * 100).toFixed(1) : "0";
  const isPositive = parseFloat(change) >= 0;

  // Format value for display (currency or number)
  const formatValue = (val: number): string => {
    if (isCurrency) {
      if (val >= 1000000) return `$${(val / 1000000).toFixed(1)}M`;
      if (val >= 1000) return `$${(val / 1000).toFixed(0)}K`;
      return `$${val.toLocaleString()}`;
    }
    return val.toLocaleString();
  };

  // Format Y-axis tick based on data type
  const formatYAxisTick = (value: number): string => {
    if (isCurrency) {
      if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
      if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
      return `$${value}`;
    }
    if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `${(value / 1000).toFixed(0)}K`;
    return value.toString();
  };

  // Get Y-axis label
  const getYAxisLabel = (): string => {
    if (isCurrency) return "Amount ($)";
    return "Count (#)";
  };

  return (
    <div style={overlay}>
      <div style={modal}>
        {/* HEADER */}
        <div style={header}>
          <h2 style={{ color: "#fff", margin: 0 }}>{title}</h2>
          <button onClick={onClose} style={closeBtn}>×</button>
        </div>

        {/* STATS */}
        <div style={statsRow}>
          <div style={statCard}>
            <p style={{ margin: 0, color: "#fff", fontSize: "12px" }}>Current</p>
            <h3 style={{ margin: "5px 0 0", fontSize: "20px" }}>
              {formatValue(currentValue)}
            </h3>
          </div>
          <div style={statCard}>
            <p style={{ margin: 0, color: "#fff", fontSize: "12px" }}>Period Start</p>
            <h3 style={{ margin: "5px 0 0", fontSize: "20px" }}>
              {formatValue(startValue)}
            </h3>
          </div>
          <div style={statCard}>
            <p style={{ margin: 0, color: "#fff", fontSize: "12px" }}>Change</p>
            <h3 style={{ 
              margin: "5px 0 0", 
              fontSize: "20px",
              color: isPositive ? "#22c55e" : "#ef4444"
            }}>
              {isPositive ? "+" : ""}{change}%
            </h3>
          </div>
        </div>

        {/* DATE RANGE */}
        <div style={rangeRow}>
          <button 
            onClick={() => handleRangeChange("7")} 
            style={{...rangeBtn, background: range === "7" ? "rgb(21 26 52)" : "#fff", color: range === "7" ? "#fff" : "#333"}}
          >
            Last 7 Days
          </button>
          <button 
            onClick={() => handleRangeChange("30")} 
            style={{...rangeBtn, background: range === "30" ? "rgb(21 26 52)" : "#fff", color: range === "30" ? "#fff" : "#333"}}
          >
            Last 30 Days
          </button>
          <button 
            onClick={() => handleRangeChange("90")} 
            style={{...rangeBtn, background: range === "90" ? "rgb(21 26 52)" : "#fff", color: range === "90" ? "#fff" : "#333"}}
          >
            Last 90 Days
          </button>
        </div>

        {/* CHART */}
        <div style={{ height: 350, padding: "0 20px 20px" }}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis 
                dataKey="date" 
                tick={{ fontSize: 11, fill: "#666" }}
                tickLine={false}
                interval="preserveStartEnd"
              />
              <YAxis 
                tick={{ fontSize: 11, fill: "#666" }}
                tickLine={false}
                axisLine={false}
                tickFormatter={formatYAxisTick}
                label={{ 
                  value: getYAxisLabel(), 
                  angle: -90, 
                  position: 'insideLeft',
                  style: { textAnchor: 'middle', fontSize: 12, fill: '#666' }
                }}
              />
              <Tooltip 
                formatter={(value: any) => {
                  if (isCurrency) {
                    return [`$${value.toLocaleString()}`, isNumberFormat ? "Count" : "Amount"];
                  }
                  return [value.toLocaleString(), "Count"];
                }}
                labelFormatter={(label) => `Date: ${label}`}
              />
              <Area
                type="monotone"
                dataKey="value"
                stroke="#4f6ef7"
                fill="#4f6ef7"
                fillOpacity={0.1}
                strokeWidth={2}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        
        {/* METRIC TYPE INDICATOR */}
        <div style={{ 
          padding: "12px 20px 20px", 
          borderTop: "1px solid #e2e8f0",
          fontSize: "12px",
          color: "#666",
          textAlign: "center"
        }}>
          <span style={{ 
            display: "inline-block",
            padding: "4px 12px",
            borderRadius: "20px",
            background: isCurrency ? "#dbeafe" : "#e0e7ff",
            color: isCurrency ? "#1d4ed8" : "#4338ca",
            fontWeight: 500
          }}>
            {isCurrency ? "💰 Currency Format ($)" : "#️⃣ Number Format (Count)"}
          </span>
        </div>
      </div>
    </div>
  );
};

export default GraphModal;

/* STYLES */
const overlay: React.CSSProperties = {
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: "rgba(0,0,0,0.6)",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  zIndex: 999
};

const modal: React.CSSProperties = {
  width: "900px",
  background: "#fff",
  borderRadius: "12px",
  overflow: "hidden",
  boxShadow: "0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)"
};

const header: React.CSSProperties = {
  background: "#1e293b",
  padding: "20px",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center"
};

const closeBtn: React.CSSProperties = {
  background: "transparent",
  color: "#fff",
  border: "none",
  fontSize: "24px",
  cursor: "pointer",
  width: "32px",
  height: "32px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "6px"
};

const statsRow: React.CSSProperties = {
  display: "flex",
  gap: "20px",
  padding: "20px",
  background: "rgb(30, 41, 59)"
};

const statCard: React.CSSProperties = {
  background: "rgb(60 65 86)",
  padding: "15px",
  borderRadius: "10px",
  flex: 1,
  color: "#fff"
};

const rangeRow: React.CSSProperties = {
  display: "flex",
  gap: "10px",
  padding: "0 20px 20px",
  marginTop: "20px"
};

const rangeBtn: React.CSSProperties = {
  padding: "8px 16px",
  border: "1px solid #e2e8f0",
  borderRadius: "10px",
  cursor: "pointer",
  background: "#fff",
  fontSize: "13px",
  fontWeight: "500",
  transition: "all 0.2s"
};